import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { DateOnly } from "@/shared/components/date-picker";
import { PeriodNavigator } from "@/shared/components/PeriodNavigator";
import { pageBgColor } from "@/utils/theme";
import { ErrorModal, getMomentTz, HelmetDexteel } from "@dexteel/mesf-core";
import { Box, Paper, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { parseAsString, useQueryState } from "nuqs";
import { useEffect, useState } from "react";
import { GasEntryTable } from "./components/GasEntryTable";
import { GasEntriesData } from "./models/GasEntry";
import {
	deleteGasEntry,
	getGasEntries,
	upsertGasEntry,
} from "./repositories/GasEntryRepository";
import { buildGasEntryDayRows } from "./utils/gasEntryRows";

const moment = getMomentTz();

export const useGasEntries = ({ month }: { month: DateOnly }) => {
	const start = moment(month, "YYYY-MM-DD").startOf("month");
	const dates = {
		startDate: start.format("YYYY-MM-DD"),
		endDate: start.clone().endOf("month").format("YYYY-MM-DD"),
	};

	return useQuery<GasEntriesData, Error>({
		queryKey: ["gas-entries", month],
		queryFn: ({ signal }) => getGasEntries(dates, signal),
	});
};

export const GasEntryPage = () => {
	const queryClient = useQueryClient();

	const currentMonth = moment().startOf("month");
	const currentMonthDate = currentMonth.format("YYYY-MM-DD");

	const [monthParam, setMonth] = useQueryState(
		"month",
		parseAsString.withDefault(currentMonthDate),
	);

	const requestedMonth = moment(monthParam, "YYYY-MM-DD", true);
	const isMonthValid =
		requestedMonth.isValid() &&
		requestedMonth.isSameOrBefore(currentMonth, "month");
	const month = isMonthValid ? monthParam : currentMonthDate;

	const [entryToDelete, setEntryToDelete] = useState<number | null>(null);
	const [error, setError] = useState("");

	const {
		data,
		isFetching,
		isError,
		error: loadError,
	} = useGasEntries({ month });

	const monthStart = moment(month, "YYYY-MM-DD").startOf("month");
	const stepMonth = (amount: number) =>
		setMonth(
			moment(monthStart.clone().add(amount, "month")).format("YYYY-MM-DD"),
		);

	const meters = data?.meters ?? [];
	const targets = data?.targets ?? [];
	const zoneShares = data?.zoneShares ?? [];
	const rows = buildGasEntryDayRows({ data, month });

	const saveEntry = useMutation(upsertGasEntry, {
		meta: { successMessage: "The day was saved successfully" },
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["gas-entries"] });
		},
		onError: async (mutationError: Error) => {
			await queryClient.invalidateQueries({ queryKey: ["gas-entries"] });
			setError(mutationError.message);
		},
	});

	const removeEntry = useMutation(deleteGasEntry, {
		meta: { successMessage: "The day was deleted successfully" },
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["gas-entries"] });
		},
		onError: (mutationError: Error) => setError(mutationError.message),
	});

	useEffect(() => {
		if (!isMonthValid) {
			setMonth(currentMonthDate);
		}
	}, [isMonthValid, currentMonthDate]);

	useEffect(() => {
		if (isError && loadError) {
			setError(loadError.message);
		}
	}, [isError, loadError]);

	return (
		<>
			<HelmetDexteel title="Gas Reading" />
			<Box
				sx={{
					bgcolor: pageBgColor,
					boxSizing: "border-box",
					display: "flex",
					flexDirection: "column",
					height: { lg: "calc(100vh - 80px)" },
					minHeight: { lg: "calc(100vh - 80px)" },
					p: { xs: 1, md: 2 },
				}}
			>
				<Typography
					component="h1"
					sx={{ fontSize: "1.5rem", fontWeight: 600, lineHeight: 1.25, mb: 1 }}
				>
					Gas Reading
				</Typography>
				<Paper component="section" elevation={2} sx={{ mb: 1, p: 1 }}>
					<PeriodNavigator
						label={monthStart.format("MMMM YYYY")}
						value={monthStart}
						views={["year", "month"]}
						maxDate={currentMonth}
						nextDisabled={monthStart.isSameOrAfter(currentMonth, "month")}
						onPrevious={() => stepMonth(-1)}
						onNext={() => stepMonth(1)}
						onCurrent={() => setMonth(currentMonthDate)}
						onChange={(value) =>
							setMonth(moment(value).startOf("month").format("YYYY-MM-DD"))
						}
					/>
				</Paper>
				<GasEntryTable
					key={month}
					rows={rows}
					meters={meters}
					targets={targets}
					zoneShares={zoneShares}
					trackedShares={data?.trackedShares ?? []}
					isLoading={isFetching || saveEntry.isPending || removeEntry.isPending}
					onSave={(draft) => saveEntry.mutateAsync(draft)}
					onDelete={setEntryToDelete}
				/>
			</Box>
			<ConfirmationDialogRaw
				open={entryToDelete !== null}
				title="Delete day"
				message="The readings of that day and its shares will be removed. This cannot be undone."
				onClose={(value?: string) => {
					if (value === "OK" && entryToDelete !== null)
						removeEntry.mutate(entryToDelete);
					setEntryToDelete(null);
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
